/**
 * Next30DaysCard — a compact Dashboard widget that shows the cashflow coming
 * due over the next 30 days, bucketed into 4 weekly bars so the user can eye
 * which week is the spike. Self-hides when nothing is due in the window.
 *
 * Props: { data: { total_amount, installment_count, weeks: [...] }, currency }
 */
import React, { useMemo } from 'react';
import { Card } from '../ui/card';
import { Calendar, ArrowRight } from 'iconoir-react';
import { formatCurrency } from './constants';

function fmtRange(startISO, endISO) {
  const fmt = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  return `${fmt(startISO)} – ${fmt(endISO)}`;
}

export default function Next30DaysCard({ data, currency }) {
  const total = data?.total_amount || 0;
  const count = data?.installment_count || 0;
  const weeks = data?.weeks || [];
  const peak = useMemo(() => weeks.reduce((m, w) => Math.max(m, w.amount || 0), 0), [weeks]);
  if (total === 0) return null;

  return (
    <Card className="card-surface p-6" data-testid="next-30-days-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-muted" strokeWidth={1.5} />
          <h2 className="font-serif text-xl text-charcoal">Next 30 days</h2>
        </div>
        <a href="/payments" className="text-terracotta hover:text-terracotta-dark text-xs flex items-center gap-1">
          View all <ArrowRight className="w-3 h-3" />
        </a>
      </div>

      <div className="flex items-baseline gap-2 mb-5">
        <span className="font-mono text-3xl text-charcoal tracking-tight" data-testid="next-30-total">
          {formatCurrency(total, currency)}
        </span>
        <span className="text-xs text-muted">
          across {count} installment{count === 1 ? '' : 's'}
        </span>
      </div>

      {/* Weekly bars — 4 buckets, height scaled to the peak week */}
      <div className="flex items-end gap-2 h-20">
        {weeks.map((w, idx) => {
          const pct = peak > 0 ? Math.max(6, (w.amount / peak) * 100) : 6;
          const empty = w.amount === 0;
          return (
            <div key={idx} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`next-30-week-${idx}`}>
              <div className="w-full flex flex-col items-center justify-end h-full">
                <div
                  className={`w-full transition-all duration-300 rounded-t-sm ${empty ? 'bg-border/60' : 'bg-terracotta/80'}`}
                  style={{ height: `${pct}%` }}
                  aria-label={`${w.label} ${formatCurrency(w.amount, currency)}`}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Labels row */}
      <div className="flex items-start gap-2 mt-2">
        {weeks.map((w, idx) => (
          <div key={idx} className="flex-1 text-center">
            <p className="font-mono text-[10px] uppercase tracking-widest text-muted">{w.label}</p>
            <p className="text-[10px] text-muted">{fmtRange(w.start, w.end)}</p>
            <p className={`font-mono text-xs mt-0.5 ${w.amount > 0 ? 'text-charcoal' : 'text-muted'}`}>
              {w.amount > 0 ? formatCurrency(w.amount, currency) : '—'}
            </p>
          </div>
        ))}
      </div>
    </Card>
  );
}
